import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SegmentedControl } from '@fly/core';
import { toastService } from '@fly/shared';
import { openDialog, useAppDispatch } from '@fly/store';

import {
  useDistributeArtifactMutation,
  useDistributeArtifactRevokeMutation,
} from '../../../../store/registry.service';
import { SKIP_DISTRIBUTION_CONFIRMATION_KEY } from '../DistributionConfirmation/DistributionConfirmation';
import { DistributionConfirmationDialogContent } from './DistributionConfirmationDialogContent';
import { DistributionFormRowLayout } from './DistributionFormRowLayout';
import { type DistributionFormRow } from './distributionFormKeys';
import { getDistributionStatusItems } from './distributionFormUtils';

function shouldSkipDistributionConfirmation() {
  if (typeof localStorage === 'undefined') {
    return false;
  }

  try {
    return localStorage.getItem(SKIP_DISTRIBUTION_CONFIRMATION_KEY) === 'true';
  } catch {
    return false;
  }
}

export function DistributionFormActiveRow({
  row,
  reopenDistributionDialog,
  disabled,
}: {
  row: DistributionFormRow;
  reopenDistributionDialog?: () => void;
  disabled?: boolean;
}) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [distributeArtifact, distributeArtifactState] =
    useDistributeArtifactMutation();
  const [revokeDistribution, revokeDistributionState] =
    useDistributeArtifactRevokeMutation();
  const busy =
    distributeArtifactState.isLoading || revokeDistributionState.isLoading;
  const controlDisabled = disabled || busy;
  const distributionStatusItems = useMemo(
    () => getDistributionStatusItems(t),
    [t]
  );

  const handleToggle = useCallback(
    async (checked: boolean) => {
      const payload = {
        packageName: row.artifactName,
        packageVersion: row.versionName,
        packageType: row.packageType,
      };

      try {
        if (checked) {
          await distributeArtifact(payload).unwrap();
          toastService.success(
            t('app.dashboard.artifacts.distribution_form.distribute_success', {
              defaultValue: 'Artifact distributed successfully',
            })
          );
        } else {
          await revokeDistribution(payload).unwrap();
          toastService.success(
            t('app.dashboard.artifacts.distribution_form.revoke_success', {
              defaultValue: 'Artifact distribution revoked successfully',
            })
          );
        }
      } catch {
        toastService.error(
          t('app.dashboard.artifacts.distribution_form.error_generic', {
            defaultValue:
              "We couldn't update the distribution. Please try again.",
          })
        );
      }
    },
    [distributeArtifact, revokeDistribution, row, t]
  );

  const requestToggle = useCallback(
    (value: boolean) => {
      if (!value) {
        void handleToggle(false);
        return;
      }
      if (shouldSkipDistributionConfirmation()) {
        void handleToggle(true);
        return;
      }
      dispatch(
        openDialog({
          width: '2',
          title: t(
            'app.dashboard.artifacts.distribution_form.distribution_confirmation.dialog_title',
            {
              defaultValue: 'Are you sure you want to distribute this?',
            }
          ),
          content: (
            <DistributionConfirmationDialogContent
              row={row}
              reopenDistributionDialog={reopenDistributionDialog}
            />
          ),
          showCloseDialog: true,
        })
      );
    },
    [dispatch, handleToggle, reopenDistributionDialog, row, t]
  );

  return (
    <DistributionFormRowLayout row={row}>
      <SegmentedControl
        variant='surface'
        size='1'
        value={row.distributed ? 'distribution' : 'not_allow'}
        items={distributionStatusItems}
        data-testid='distribution-segmented-control'
        disabled={controlDisabled}
        onChange={(value) => {
          requestToggle(value === 'distribution');
        }}
      />
    </DistributionFormRowLayout>
  );
}
